import React, { useState } from 'react'
import { Box, Collapse, Paper, Typography } from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { ACTIVITY_TYPE_COLORS, advancedColors } from '../theme'
import { ActivityBlock } from '../activityBlock'

type Props = {
  block: ActivityBlock
  size?: 'compact' | 'comfortable'
}

const ActivityBlockCard = ({ block, size = 'compact' }: Props) => {
  const [open, setOpen] = useState(false)
  const toggle = () => setOpen((value) => !value)
  const comfortable = size === 'comfortable'

  return (
    <Paper
      sx={{
        borderLeft: `4px solid ${ACTIVITY_TYPE_COLORS[block.type]}`,
        p: comfortable ? 2 : 1.25,
        mb: comfortable ? 1.5 : 1,
        '&:last-child': { mb: 0 },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            toggle()
          }
        }}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, cursor: 'pointer' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={comfortable ? 'h6' : 'subtitle1'} sx={{ fontWeight: 600, color: advancedColors.ink }}>
            {block.title}
          </Typography>
          {block.indicators.length > 0 && (
            <Typography variant={comfortable ? 'subtitle1' : 'subtitle2'} sx={{ color: advancedColors.inkMuted }}>
              {block.indicators.map((indicator) => `${indicator.label}: ${indicator.value}`).join(' · ')}
            </Typography>
          )}
        </Box>
        <ExpandMoreRoundedIcon
          fontSize={comfortable ? 'medium' : 'small'}
          sx={{
            color: advancedColors.inkFaint,
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 120ms',
          }}
        />
      </Box>
      <Collapse in={open}>
        <Box sx={{ mt: comfortable ? 1.5 : 1 }}>
          <Typography variant={comfortable ? 'body1' : 'body2'} sx={{ color: advancedColors.ink }}>
            {block.description}
          </Typography>
          {block.details && block.details.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: comfortable ? 1 : 0.75, pl: 2.5 }}>
              {block.details.map((detail) => (
                <Typography
                  component="li"
                  variant={comfortable ? 'body1' : 'body2'}
                  key={detail}
                  sx={{ color: advancedColors.inkMuted }}
                >
                  {detail}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  )
}

export default ActivityBlockCard
